import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { vehiclesAPI, apiUtils } from '@/services/api';

// État initial
const initialState = {
  // Véhicules
  vehicles: [],
  vehiclesLoading: false,
  vehiclesError: null,
  vehiclesPagination: null,
  
  // Types de véhicules
  vehicleTypes: [],
  vehicleTypesLoading: false,
  vehicleTypesError: null,
  
  // Statistiques
  vehicleStatistics: null,
  statisticsLoading: false,
  statisticsError: null,
  
  // Filtres et recherche
  filters: {
    search: '',
    status: '',
    vehicle_type: '',
    insurance_status: '',
    technical_status: '',
    transporter_id: null,
  },
  
  // Pagination
  pagination: {
    page: 1,
    pageSize: 20,
    total: 0,
  },
  
  // Actions en cours
  actions: {
    creating: false,
    updating: false,
    deleting: false,
  },
};

export const useVehiclesStore = create(
  devtools(
    (set, get) => ({
      ...initialState,
      
      // ===== ACTIONS VÉHICULES =====
      
      // Charger les véhicules
      loadVehicles: async (params = {}) => {
        set({ vehiclesLoading: true, vehiclesError: null });
        
        try {
          const currentFilters = get().filters;
          const currentPagination = get().pagination;
          
          const queryParams = {
            ...currentFilters,
            ...params,
            page: params.page || currentPagination.page,
            page_size: params.pageSize || currentPagination.pageSize,
          };
          
          // Nettoyer les paramètres vides
          Object.keys(queryParams).forEach(key => {
            if (queryParams[key] === '' || queryParams[key] === 'all' || queryParams[key] === null || queryParams[key] === undefined) {
              delete queryParams[key];
            }
          });
          
          const response = await vehiclesAPI.getVehicles(queryParams);
          
          set({
            vehicles: response.data.results || response.data,
            vehiclesLoading: false,
            vehiclesPagination: response.data.pagination || null,
            pagination: {
              ...currentPagination,
              page: queryParams.page || 1,
              pageSize: queryParams.page_size || 20,
              total: response.data.count || (response.data.results ? response.data.results.length : 0),
            },
          });
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({
            vehiclesLoading: false,
            vehiclesError: apiError,
          });
          throw apiError;
        }
      },
      
      // Charger les véhicules d'un transporteur
      loadVehiclesByTransporter: async (transporterId, params = {}) => {
        set({ vehiclesLoading: true, vehiclesError: null });
        
        try {
          const response = await vehiclesAPI.getVehiclesByTransporter(transporterId, params);
          
          set({
            vehicles: response.data.vehicles || response.data,
            vehiclesLoading: false,
            vehicleStatistics: response.data.statistics || null,
          });
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({
            vehiclesLoading: false,
            vehiclesError: apiError,
          });
          throw apiError;
        }
      },
      
      // Charger un véhicule par ID
      loadVehicleById: async (id) => {
        try {
          const response = await vehiclesAPI.getVehicleById(id);
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          throw apiError;
        }
      },
      
      // Créer un véhicule
      createVehicle: async (vehicleData) => {
        set({ actions: { ...get().actions, creating: true } });
        
        try {
          // Validation côté client
          const validation = apiUtils.validateVehicleData(vehicleData);
          if (!validation.isValid) {
            throw {
              type: 'validation',
              message: 'Données invalides',
              details: validation.errors,
            };
          }
          
          const response = await vehiclesAPI.createVehicle(vehicleData);
          
          // Ajouter le nouveau véhicule à la liste
          set(state => ({
            vehicles: [response.data, ...state.vehicles],
            actions: { ...state.actions, creating: false },
          }));
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({ actions: { ...get().actions, creating: false } });
          throw apiError;
        }
      },
      
      // Mettre à jour un véhicule
      updateVehicle: async (id, vehicleData) => {
        set({ actions: { ...get().actions, updating: true } });
        
        try {
          const response = await vehiclesAPI.updateVehicle(id, vehicleData);
          
          // Mettre à jour le véhicule dans la liste
          set(state => ({
            vehicles: state.vehicles.map(vehicle =>
              vehicle.id === id ? response.data : vehicle
            ),
            actions: { ...state.actions, updating: false },
          }));
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({ actions: { ...get().actions, updating: false } });
          throw apiError;
        }
      },
      
      // Supprimer un véhicule
      deleteVehicle: async (id) => {
        set({ actions: { ...get().actions, deleting: true } });
        
        try {
          await vehiclesAPI.deleteVehicle(id);
          
          // Retirer le véhicule de la liste
          set(state => ({
            vehicles: state.vehicles.filter(vehicle => vehicle.id !== id),
            actions: { ...state.actions, deleting: false },
          }));
          
          return true;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({ actions: { ...get().actions, deleting: false } });
          throw apiError;
        }
      },
      
      // ===== ACTIONS TYPES DE VÉHICULES =====
      
      // Charger les types de véhicules
      loadVehicleTypes: async () => {
        set({ vehicleTypesLoading: true, vehicleTypesError: null });
        
        try {
          const response = await vehiclesAPI.getVehicleTypes();
          
          set({
            vehicleTypes: response.data,
            vehicleTypesLoading: false,
          });
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({
            vehicleTypesLoading: false,
            vehicleTypesError: apiError,
          });
          throw apiError;
        }
      },
      
      // ===== ACTIONS STATISTIQUES =====
      
      // Charger les statistiques
      loadStatistics: async (params = {}) => {
        set({ statisticsLoading: true, statisticsError: null });
        
        try {
          const response = await vehiclesAPI.getVehicleStatistics(params);
          
          set({
            vehicleStatistics: response.data,
            statisticsLoading: false,
          });
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({
            statisticsLoading: false,
            statisticsError: apiError,
          });
          throw apiError;
        }
      },
      
      // ===== ACTIONS FILTRES ET RECHERCHE =====
      
      // Mettre à jour les filtres
      updateFilters: (newFilters) => {
        set(state => ({
          filters: { ...state.filters, ...newFilters },
          pagination: { ...state.pagination, page: 1 }, // Reset à la page 1
        }));
      },
      
      // Réinitialiser les filtres
      resetFilters: () => {
        set({
          filters: initialState.filters,
          pagination: { ...get().pagination, page: 1 },
        });
      },
      
      // Mettre à jour la pagination
      updatePagination: (pagination) => {
        set(state => ({
          pagination: { ...state.pagination, ...pagination },
        }));
      },
      
      // ===== ACTIONS UTILITAIRES =====
      
      // Réinitialiser l'état
      reset: () => {
        set(initialState);
      },
      
      // Réinitialiser les erreurs
      clearErrors: () => {
        set({
          vehiclesError: null,
          vehicleTypesError: null,
          statisticsError: null,
        });
      },
      
      // Formater les données des véhicules
      getFormattedVehicles: () => {
        const vehicles = get().vehicles;
        return vehicles.map(vehicle => apiUtils.formatVehicleData(vehicle));
      },
      
      // Obtenir un véhicule par ID depuis le store
      getVehicleById: (id) => {
        const vehicles = get().vehicles;
        return vehicles.find(vehicle => vehicle.id === id);
      },
      
      // Vérifier si un véhicule est en cours de traitement
      isVehicleProcessing: (id) => {
        const actions = get().actions;
        return actions.creating || actions.updating || actions.deleting;
      },
    }),
    {
      name: 'vehicles-store',
    }
  )
);
